/**
 * Application shell: which screen is up, who is playing, and when a finished run
 * gets written to the scoreboard.
 *
 * This is the layer that is allowed to know about the browser (it reads the
 * clock for timestamps and owns a `ScoreRepository`). `core/` stays ignorant of
 * all of it — the shell only calls `GameState`'s public API and listens to its
 * events.
 */

import { Action, Phase } from '../core/game.ts';
import {
  SCOREBOARD,
  bestScore,
  finalizeName,
  insertEntry,
  personalBest,
  sanitizeName,
  type ScoreEntry,
  type ScoreRepository,
} from '../core/scoreboard.ts';
/**
 * What the shell needs from an input layer. `KeyboardInput` satisfies it; a
 * test stub or a gamepad layer can too.
 */
export interface GameInput {
  update(dtMs: number): void;
  keyDown(event: KeyboardEvent): void;
  keyUp(event: KeyboardEvent): void;
  releaseAll(): void;
}
import {
  MenuRow,
  SCORE_FILTERS,
  type MenuView,
  type ScoreFilter,
  type ScoreboardView,
} from '../render/menu.ts';
import type { GameView } from '../render/canvas.ts';
import { activeShell, setShell, type ShellTheme } from '../render/theme.ts';
import type { HitId } from '../render/hits.ts';
import { GameMode, GameSession } from './session.ts';

export enum Screen {
  Menu = 'MENU',
  Game = 'GAME',
  Scoreboard = 'SCOREBOARD',
}

export interface ShellOptions {
  session: GameSession;
  input: GameInput;
  repository: ScoreRepository;
  /** Injectable so tests do not depend on the wall clock. */
  now?: () => number;
  /** Reported to the menu footer. */
  persistent?: boolean;
  /** Called whenever the shell theme changes, so the page can follow it. */
  onThemeChange?: (theme: ShellTheme) => void;
}

export class Shell {
  screen: Screen = Screen.Menu;
  menuRow: MenuRow = MenuRow.Name;
  /** Name as typed on the menu, before finalisation. */
  nameDraft: string;
  /** Name the current run is played under. */
  playerName: string = SCOREBOARD.fallbackName;
  gameMode: GameMode = GameMode.Solo;
  /** Which slice of the board the scoreboard is showing. */
  boardFilter: ScoreFilter = 'ALL';

  private entries: ScoreEntry[];
  private lastEntry: ScoreEntry | null = null;
  private lastRank: number | null = null;
  /** Guards against saving the same run twice. */
  private savedThisRun = false;

  private readonly session: GameSession;
  private readonly input: GameInput;
  private readonly repository: ScoreRepository;
  private readonly now: () => number;
  private readonly persistent: boolean;
  private readonly onThemeChange: (theme: ShellTheme) => void;

  constructor(options: ShellOptions) {
    this.session = options.session;
    this.input = options.input;
    this.repository = options.repository;
    this.now = options.now ?? (() => Date.now());
    this.persistent = options.persistent ?? true;

    this.onThemeChange = options.onThemeChange ?? (() => {});

    this.entries = this.repository.load();
    this.nameDraft = sanitizeName(this.repository.loadName?.() ?? '');

    const stored = this.repository.loadTheme?.();
    if (stored === 'night' || stored === 'day') setShell(stored);
    this.onThemeChange(activeShell());

    this.session.events.on('onComplete', () => this.recordRun());
  }

  // ------------------------------------------------------------------- theme

  get theme(): ShellTheme {
    return activeShell();
  }

  /** Swap Night ↔ Day. Bound to F2 on every screen. */
  toggleTheme(): void {
    const next: ShellTheme = activeShell() === 'night' ? 'day' : 'night';
    setShell(next);
    this.repository.saveTheme?.(next);
    this.onThemeChange(next);
  }

  // ------------------------------------------------------------------- views

  menuView(): MenuView {
    return {
      nameDraft: this.nameDraft,
      selected: this.menuRow,
      mode: this.gameMode,
      // The preview shows the mode you are about to play, not a mixed table.
      entries: entriesFor(this.entries, this.gameMode),
      personalBest: personalBest(
        entriesFor(this.entries, this.gameMode),
        finalizeName(this.nameDraft),
      ),
      persistent: this.persistent,
    };
  }

  scoreboardView(): ScoreboardView {
    return {
      entries: entriesFor(this.entries, this.boardFilter),
      filter: this.boardFilter,
      highlight: this.lastEntry,
      persistent: this.persistent,
    };
  }

  /** Move the scoreboard filter. Bound to ←→ and to the tab hit regions. */
  setBoardFilter(filter: ScoreFilter): void {
    this.boardFilter = filter;
  }

  cycleBoardFilter(delta: number): void {
    const index =
      (SCORE_FILTERS.indexOf(this.boardFilter) + delta + SCORE_FILTERS.length) %
      SCORE_FILTERS.length;
    this.boardFilter = SCORE_FILTERS[index];
  }

  gameView(): GameView {
    return {
      playerName: this.playerName,
      best: bestScore(entriesFor(this.entries, this.gameMode)),
      rank: this.lastRank,
      mode: this.gameMode,
    };
  }

  // ---------------------------------------------------------------- commands

  startGame(): void {
    this.playerName = finalizeName(this.nameDraft);
    this.nameDraft = this.playerName;
    this.repository.saveName?.(this.playerName);
    this.lastEntry = null;
    this.lastRank = null;
    this.savedThisRun = false;
    this.screen = Screen.Game;
    this.session.start(this.gameMode);
  }

  restartGame(): void {
    this.lastEntry = null;
    this.lastRank = null;
    this.savedThisRun = false;
    this.session.restart();
  }

  openMenu(): void {
    this.input.releaseAll();
    this.screen = Screen.Menu;
    this.menuRow = MenuRow.Start;
  }

  openScoreboard(): void {
    this.input.releaseAll();
    this.screen = Screen.Scoreboard;
  }

  /** Advance the parts of the app that need time. */
  step(dtMs: number): void {
    if (this.screen !== Screen.Game) return;
    this.input.update(dtMs);
    this.session.step(dtMs);
  }

  // ------------------------------------------------------------------- input

  /** Single entry point for keydown, routed by screen. */
  keyDown(event: KeyboardEvent): void {
    // The theme switch is global; F2 is not a printable key, so it never
    // collides with typing a name on the menu.
    if (event.code === 'F2') {
      event.preventDefault();
      this.toggleTheme();
      return;
    }

    switch (this.screen) {
      case Screen.Menu:
        this.menuKeyDown(event);
        return;
      case Screen.Scoreboard:
        this.scoreboardKeyDown(event);
        return;
      case Screen.Game:
        this.gameKeyDown(event);
        return;
    }
  }

  keyUp(event: KeyboardEvent): void {
    if (this.screen === Screen.Game) this.input.keyUp(event);
  }

  // ----------------------------------------------------------------- pointer

  /**
   * Activate the control under the pointer. The renderers record where they
   * painted each control; the host looks the point up and passes the id here,
   * so this layer never has to know any geometry.
   */
  pointerDown(id: HitId | null): void {
    switch (id) {
      case null:
        return;
      case 'theme':
        this.toggleTheme();
        return;
      case 'menu.name':
        this.menuRow = MenuRow.Name;
        return;
      case 'menu.mode.solo':
        this.gameMode = GameMode.Solo;
        this.menuRow = MenuRow.Mode;
        return;
      case 'menu.mode.balance':
        this.gameMode = GameMode.Balance;
        this.menuRow = MenuRow.Mode;
        return;
      case 'menu.mode.twin':
        this.gameMode = GameMode.Twin;
        this.menuRow = MenuRow.Mode;
        return;
      case 'menu.start':
      case 'board.play':
        this.startGame();
        return;
      case 'menu.scoreboard':
      case 'over.scoreboard':
        this.openScoreboard();
        return;
      case 'board.filter.all':
        this.boardFilter = 'ALL';
        return;
      case 'board.filter.solo':
        this.boardFilter = 'SOLO';
        return;
      case 'board.filter.balance':
        this.boardFilter = 'BALANCE';
        return;
      case 'board.filter.twin':
        this.boardFilter = 'TWIN';
        return;
      case 'board.back':
      case 'pause.menu':
      case 'over.menu':
        this.openMenu();
        return;
      case 'game.start':
        this.session.applyInput(Action.Start);
        return;
      case 'game.pause':
      case 'pause.resume':
        this.session.applyInput(Action.Pause);
        return;
      case 'pause.restart':
      case 'over.restart':
        this.restartGame();
        return;
    }
  }

  /** Hovering a menu row moves the cursor onto it, so both inputs agree. */
  pointerMove(id: HitId | null): void {
    if (this.screen !== Screen.Menu) return;
    if (id === 'menu.name') this.menuRow = MenuRow.Name;
    else if (
      id === 'menu.mode.solo' ||
      id === 'menu.mode.balance' ||
      id === 'menu.mode.twin'
    ) this.menuRow = MenuRow.Mode;
    else if (id === 'menu.start') this.menuRow = MenuRow.Start;
    else if (id === 'menu.scoreboard') this.menuRow = MenuRow.Scoreboard;
  }

  blur(): void {
    this.input.releaseAll();
    if (this.screen === Screen.Game && this.session.phase === Phase.Playing) {
      this.session.applyInput(Action.Pause);
    }
  }

  private menuKeyDown(event: KeyboardEvent): void {
    switch (event.code) {
      case 'ArrowUp':
        event.preventDefault();
        this.menuRow = cycleRow(this.menuRow, -1);
        return;
      case 'ArrowDown':
      case 'Tab':
        event.preventDefault();
        this.menuRow = cycleRow(this.menuRow, 1);
        return;
      case 'ArrowLeft':
      case 'ArrowRight':
        if (this.menuRow !== MenuRow.Mode) break;
        event.preventDefault();
        this.gameMode = cycleMode(this.gameMode, event.code === 'ArrowRight' ? 1 : -1);
        return;
      case 'Enter':
      case 'NumpadEnter':
        event.preventDefault();
        this.activateMenuRow();
        return;
      case 'Backspace':
        event.preventDefault();
        this.menuRow = MenuRow.Name;
        this.nameDraft = this.nameDraft.slice(0, -1);
        return;
      default:
        break;
    }

    // Printable characters edit the name from any row, and pull focus to it.
    if (event.key.length !== 1 || event.ctrlKey || event.metaKey || event.altKey) return;
    const next = sanitizeName(this.nameDraft + event.key);
    if (next === this.nameDraft) return;
    event.preventDefault();
    this.menuRow = MenuRow.Name;
    this.nameDraft = next;
  }

  private activateMenuRow(): void {
    switch (this.menuRow) {
      case MenuRow.Name:
        this.menuRow = MenuRow.Mode;
        return;
      case MenuRow.Mode:
        this.menuRow = MenuRow.Start;
        return;
      case MenuRow.Start:
        this.startGame();
        return;
      case MenuRow.Scoreboard:
        this.openScoreboard();
        return;
    }
  }

  private scoreboardKeyDown(event: KeyboardEvent): void {
    switch (event.code) {
      case 'ArrowLeft':
        event.preventDefault();
        this.cycleBoardFilter(-1);
        return;
      case 'ArrowRight':
        event.preventDefault();
        this.cycleBoardFilter(1);
        return;
      case 'Escape':
      case 'KeyM':
      case 'Backspace':
        event.preventDefault();
        this.openMenu();
        return;
      case 'Enter':
      case 'NumpadEnter':
        event.preventDefault();
        this.startGame();
        return;
      default:
        return;
    }
  }

  private gameKeyDown(event: KeyboardEvent): void {
    if (this.session.phase === Phase.GameOver) {
      switch (event.code) {
        case 'KeyR':
          event.preventDefault();
          this.restartGame();
          return;
        case 'KeyS':
        case 'Enter':
        case 'NumpadEnter':
          event.preventDefault();
          this.openScoreboard();
          return;
        case 'KeyM':
        case 'Escape':
          event.preventDefault();
          this.openMenu();
          return;
        default:
          return;
      }
    }

    if (this.session.phase === Phase.Paused && event.code === 'KeyM') {
      event.preventDefault();
      this.openMenu();
      return;
    }

    this.input.keyDown(event);
  }

  // ------------------------------------------------------------------- saving

  /** Write the finished run to the board. Runs once per game over. */
  private recordRun(): void {
    if (this.savedThisRun) return;
    this.savedThisRun = true;
    if (this.session.score <= 0) return;

    const entry: ScoreEntry = {
      name: this.playerName,
      score: this.session.score,
      lines: this.session.lines,
      level: this.session.level,
      playedAt: this.now(),
      mode: this.gameMode,
    };
    const result = insertEntry(this.entries, entry, SCOREBOARD.capacity);
    this.entries = result.entries;
    this.lastEntry = result.rank === null ? null : entry;
    this.lastRank = result.rank;
    this.repository.save(this.entries);
  }
}

/** Legacy entries carry no `mode` and count as solo runs. */
function entriesFor(entries: readonly ScoreEntry[], filter: ScoreFilter): ScoreEntry[] {
  if (filter === 'ALL') return [...entries];
  return entries.filter((entry) => (entry.mode ?? 'SOLO') === filter);
}

function cycleRow(row: MenuRow, delta: number): MenuRow {
  const rows = [MenuRow.Name, MenuRow.Mode, MenuRow.Start, MenuRow.Scoreboard];
  const index = (rows.indexOf(row) + delta + rows.length) % rows.length;
  return rows[index];
}

function cycleMode(mode: GameMode, delta: number): GameMode {
  const modes = [GameMode.Solo, GameMode.Balance, GameMode.Twin];
  const index = (modes.indexOf(mode) + delta + modes.length) % modes.length;
  return modes[index];
}
